import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "shared/Button/Button";
import {
  addToBookingCart,
  setIsBookingCartOpen,
  removeFromBookingCart,
  emptyBookingCart,
  emptyBookingStaff,
  emptyBookingTime,
  emptyPaymentMethod,
  resetBookingState,
} from "../../../redux/features/cartStateBooking"; // ← your slice

/* -------------------- Helpers -------------------- */
const formatDuration = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h} hr ${m} mins`;
  if (h) return `${h} hr`;
  return `${m} mins`;
};

const parseMinutes = (desc = "") => {
  const d = String(desc).toLowerCase(); // e.g. "30 mins", "1 hr 15 mins"
  const hr = d.match(/(\d+)\s*(hr|hrs|hour|hours)/i);
  const mi = d.match(/(\d+)\s*(min|mins|minute|minutes)/i);
  return (hr ? parseInt(hr[1], 10) : 0) * 60 + (mi ? parseInt(mi[1], 10) : 0);
};

const toPriceNumber = (val) => {
  if (typeof val === "number") return val;
  const n = parseFloat(String(val).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  }).format(n || 0);

const buildCategoryMap = (serviceCategory = []) => {
  return serviceCategory.reduce((m, c) => {
    if (!c?._id) return m;
    m[c._id] = {
      label: c.name || c.title || c.label || `Category ${c._id}`,
      image:
        c.image || c.photo || c.coverPhoto || c.iconUrl || c.thumbnail || null,
    };
    return m;
  }, {});
};

const highlightMatch = (text, keyword) => {
  if (!keyword) return text;
  const regex = new RegExp(`(${keyword})`, "gi");
  return text
    .split(regex)
    .map((part) =>
      part.toLowerCase() === keyword.toLowerCase()
        ? `<mark style="background-color:#f87171;color:#000;">${part}</mark>`
        : part
    )
    .join("");
};

/* -------------------- Component -------------------- */
const MultiSelectModal = ({
  label,
  options, // services: { _id, title, desc, price, category, ... }
  serviceCategory, // categories: [{ _id, name, ... }]
  selectedValues, // store RAW service objects here
  setSelectedValues,
  selectedId,
  setSelectedId,
  totalTime,
  setTotalTime,
  operatorId, // not stored in cart now, but kept if you need later
  operatorSlug, // same
  staffId, // same
  selectedDateTime, // same
}) => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(options[0]?.category);
  const [searchTerm, setSearchTerm] = useState("");

  const categoryRefs = useRef({});
  const tabListRef = useRef(null);
  const scrollContainerRef = useRef(null); // observer root

  const bookingCart = useSelector((s) => s.cartBooking.bookingCart);

  // derive selected + totals from the cart (since you store RAW services in bookingCart)
  const selectedFromCart = useMemo(() => bookingCart, [bookingCart]);

  const totalsFromCart = useMemo(() => {
    let minutes = 0;
    let price = 0;
    for (const it of selectedFromCart) {
      // RAW service shape: { _id, title, desc, price, ... }
      minutes += parseMinutes(it.desc);
      price += toPriceNumber(it.price);
    }
    return { minutes, price };
  }, [selectedFromCart]);

  const catMap = useMemo(
    () => buildCategoryMap(serviceCategory),
    [serviceCategory]
  );

  // console.log({ catMap });

  // group options by category id
  const groupedOptions = useMemo(() => {
    return (options || []).reduce((acc, item) => {
      const key =
        typeof item.category === "string"
          ? item.category
          : item.category?._id || "uncategorized";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [options]);

  // filter by search term
  const filteredOptions = useMemo(() => {
    const out = {};
    Object.keys(groupedOptions).forEach((catId) => {
      const list = groupedOptions[catId].filter((it) =>
        it.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (list.length) out[catId] = list;
    });
    return out;
  }, [groupedOptions, searchTerm]);

  // keep activeCategory valid when filter set changes
  useEffect(() => {
    const rootEl = scrollContainerRef.current;
    if (!rootEl) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best = null;
        for (const e of entries) {
          if (e.isIntersecting) {
            if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
          }
        }
        if (best) {
          const cat = best.target.getAttribute("data-category");
          if (cat && cat !== activeCategory) setActiveCategory(cat);
        }
      },
      {
        root: rootEl,
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: "-30% 0px -55% 0px",
      }
    );

    Object.keys(filteredOptions).forEach((catId) => {
      const el = categoryRefs.current[catId];
      if (el) io.observe(el);
    });

    return () => io.disconnect();
  }, [filteredOptions, activeCategory]);

  // observe sections INSIDE the scroll container
  useEffect(() => {
    const rootEl = scrollContainerRef.current;
    if (!rootEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const catId = entry.target.getAttribute("data-category");
            if (catId) {
              setActiveCategory(catId);

              // Always align active tab to start (flush left)
              const btn = tabListRef.current?.querySelector(
                `button[data-tab="${catId}"]`
              );
              if (btn && tabListRef.current) {
                const container = tabListRef.current;
                container.scrollTo({
                  left: btn.offsetLeft,
                  behavior: "smooth",
                });
              }
            }
          }
        });
      },
      {
        root: rootEl,
        threshold: 0.4,
      }
    );

    Object.keys(filteredOptions).forEach((catId) => {
      const el = categoryRefs.current[catId];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [filteredOptions]);

  // center active tab in the horizontal tab strip
  useEffect(() => {
    const wrap = tabListRef.current;
    if (!wrap || !activeCategory) return;
    const btn = wrap.querySelector(`button[data-tab="${activeCategory}"]`);
    if (btn) {
      btn.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [activeCategory]);

  // sync selected ids to parent
  useEffect(() => {
    if (!setSelectedId) return;
    setSelectedId(selectedValues.map((v) => v._id));
  }, [selectedValues, setSelectedId]);

  // totals (duration + price) computed from RAW items in selectedValues
  const totals = useMemo(() => {
    let minutes = 0;
    let price = 0;
    for (const it of selectedValues) {
      minutes += parseMinutes(it.desc);
      price += toPriceNumber(it.price);
    }
    return { minutes, price };
  }, [selectedValues]);

  useEffect(() => {
    if (setTotalTime) setTotalTime(totals.minutes);
  }, [totals.minutes, setTotalTime]);

  const toggleSelection = (item) => {
    const already = selectedValues.some((v) => v._id === item._id);

    setSelectedValues((prev) =>
      already ? prev.filter((v) => v._id !== item._id) : [...prev, item]
    );

    if (already) {
      dispatch(removeFromBookingCart({ _id: item._id }));
    } else {
      dispatch(addToBookingCart({ item })); // store the raw service doc
      // dispatch(setIsBookingCartOpen(true));
    }

    setActiveCategory(item.category);
  };

  // scroll inside the list when clicking a tab
  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    const el = categoryRefs.current[catId];
    const rootEl = scrollContainerRef.current;
    if (el && rootEl) {
      rootEl.scrollTo({
        top: el.offsetTop - 8,
        behavior: "smooth",
      });
    }
  };

  const summaryText =
    selectedValues.length === 0
      ? "Click box to select services"
      : `${selectedValues.length} selected • ${formatCurrency(
          totals.price
        )} • ${formatDuration(totals.minutes)}`;

  return (
    <div className="space-y-2">
      <h2 className="text-2xl text-text font-bold mt-8">{label}</h2>

      {/* Trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className={`w-full p-3 rounded-lg border text-left min-h-[2.5rem] ${
          selectedValues.length > 0
            ? "border-redlight"
            : "border-border text-gray-400"
        }`}
      >
        {selectedValues.length === 0 ? (
          <p className="text-textGray text-sm italic">{summaryText}</p>
        ) : (
          <>
            {selectedValues.map((selected) => (
              <div
                key={selected._id}
                className="font-medium text-text flex lg:items-center sm:items-start"
              >
                <div className="w-6 h-6 bg-redlight rounded-md flex items-center justify-center m-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="ml-2 text-lg">
                  {selected.title}
                  {selected.desc && (
                    <span className="text-md text-gray-500">
                      {" "}
                      ({selected.desc})
                    </span>
                  )}
                </p>
              </div>
            ))}

            <p className="text-redlight font-medium text-right mt-2">
              <span className="text-textGray">Total:</span>{" "}
              <span className="font-semibold">
                {formatCurrency(totals.price)}
              </span>{" "}
              <span className="text-textGray">•</span>{" "}
              {formatDuration(totals.minutes)}
            </p>
          </>
        )}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]">
          <div
            className="bg-dry border border-redlight w-[95vw] max-w-2xl h-[85vh] rounded-xl p-4 flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-redlight mb-3">{label}</h2>

            <input
              type="text"
              placeholder="🔍 Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="placeholder-textGray w-full p-2 mb-4 rounded-lg border border-border bg-drylight text-text focus:outline-none focus:ring-2 focus:ring-redlight text-sm"
            />

            {/* Tabs */}
            <div
              ref={tabListRef}
              className="flex overflow-x-auto scrollbar-thin space-x-2 py-2 mb-4"
            >
              {Object.keys(filteredOptions).map((catId) => (
                <button
                  key={catId}
                  data-tab={catId}
                  onClick={() => handleCategoryClick(catId)}
                  className={`px-4 py-1 rounded-full text-md whitespace-nowrap ${
                    catId === activeCategory
                      ? "bg-redlight text-white font-semibold"
                      : "bg-none text-text"
                  }`}
                >
                  {catMap[catId]?.label || `Category ${catId}`}
                </button>
              ))}
            </div>

            {/* List */}
            <div
              ref={scrollContainerRef}
              className="overflow-y-auto scrollbar-thin flex-1 px-1 mb-4"
            >
              {Object.keys(filteredOptions).length ? (
                Object.keys(filteredOptions).map((catId) => (
                  <div
                    key={catId}
                    data-category={catId}
                    ref={(el) => (categoryRefs.current[catId] = el)}
                    className="mb-6"
                  >
                    <div className="flex justify-between my-3">
                      <h3 className="text-xl font-bold text-redlight mb-3 uppercase">
                        {catMap[catId]?.label || `Category ${catId}`}
                      </h3>
                      <div className="w-[50%] sm:max-w-full max-w-[50%]">
                        <img
                          src={catMap[catId].image}
                          alt={catMap[catId].label}
                          className="w-full object-cover mx-auto sm:max-h-[70px] lg:max-h-[150px]"
                        />
                      </div>
                    </div>

                    {filteredOptions[catId].map((item) => {
                      const isSelected = selectedValues.some(
                        (v) => v._id === item._id
                      );
                      const priceNum = toPriceNumber(item.price);
                      const minutes = parseMinutes(item.desc);

                      return (
                        <div
                          key={item._id}
                          onClick={() => toggleSelection(item)}
                          className={`group relative flex items-start justify-between gap-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer mb-4
                            ${
                              isSelected
                                ? "border-redlight bg-redlight text-white hover:bg-red-700 shadow-md"
                                : "border-border bg-drylight text-text hover:border-redlight hover:shadow-sm hover:scale-[1.01]"
                            }`}
                        >
                          {/* Left: title + desc */}
                          <div className="flex-1">
                            <span
                              className={`text-md sm:text-md font-semibold leading-snug  ${
                                isSelected ? "text-white" : "text-textGray"
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: highlightMatch(item.title, searchTerm),
                              }}
                            />
                            {item.desc && (
                              <span
                                className={`text-sm ${
                                  isSelected ? "text-white/80" : "text-textGray"
                                }`}
                              >
                                &nbsp;({item.desc})
                              </span>
                            )}
                            <div className="flex items-center gap-3 mt-1">
                              <span
                                className={`text-lg font-bold ${
                                  isSelected ? "text-white" : "text-text"
                                }`}
                              >
                                {formatCurrency(priceNum)}
                              </span>
                            </div>
                          </div>

                          {/* Right: price + duration + check */}
                          <div className="flex flex-col items-end gap-2 shrink-0">
                            <div
                              className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200
                                ${
                                  isSelected
                                    ? "bg-gray-300 text-reddark border border-reddark"
                                    : "border border-textGray group-hover:border-redlight"
                                }`}
                            >
                              {isSelected && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={5}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center text-redlight text-2xl mt-10 opacity-0 animate-fadeIn">
                  No services found ❌
                </div>
              )}
            </div>

            {/* Footer summary + Clear + Done */}
            <div className="sticky bottom-0 left-0 right-0 bg-dry border-t border-border -mx-4 px-4 pt-3 pb-4">
              <div className="flex flex-col sm:flex-row items-center lg:justify-between justify-center gap-3">
                <div className="text-sm sm:text-base">
                  <span className="text-textGray">
                    {selectedValues.length} selected •{" "}
                  </span>
                  <span className="">{formatCurrency(totals.price)}</span>
                  <span className="text-textGray"> •</span>
                  <span className="text-white">
                    {" "}
                    {formatDuration(totals.minutes)}
                  </span>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    className="text-sm px-4 py-2 bg-none text-redlight hover:text-text rounded-md hover:border hover:border-text"
                    onClick={() => {
                      setSelectedValues([]);
                      dispatch(resetBookingState());
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    className="text-sm px-4 py-2 bg-reddark text-white hover:text-redlight hover:bg-transparent hover:border hover:border-redlight rounded-md"
                    onClick={() => setIsOpen(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>

            {/* Close */}
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectModal;
